import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trash2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Props { ownerId: string; }

interface Spot { id: string; address: string; }
interface Blackout {
  id: string;
  parking_spot_id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
}

const BlackoutsTab = ({ ownerId }: Props) => {
  const { toast } = useToast();
  const [spots, setSpots] = useState<Spot[]>([]);
  const [blackouts, setBlackouts] = useState<Blackout[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ parking_spot_id: "", start_date: "", end_date: "", reason: "" });

  const load = async () => {
    const { data: s } = await supabase.from("parking_spots").select("id, address").eq("owner_id", ownerId);
    setSpots(s ?? []);
    const { data: b } = await supabase
      .from("spot_blackouts")
      .select("*")
      .eq("owner_id", ownerId)
      .order("start_date", { ascending: true });
    setBlackouts((b as Blackout[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [ownerId]);

  const add = async () => {
    if (!form.parking_spot_id || !form.start_date || !form.end_date) {
      toast({ title: "Missing fields", variant: "destructive" });
      return;
    }
    const { error } = await supabase.from("spot_blackouts").insert({
      ...form,
      owner_id: ownerId,
      reason: form.reason || null,
    });
    if (error) return toast({ title: "Failed", description: error.message, variant: "destructive" });
    toast({ title: "Blackout added" });
    setForm({ parking_spot_id: "", start_date: "", end_date: "", reason: "" });
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("spot_blackouts").delete().eq("id", id);
    if (error) return toast({ title: "Failed", variant: "destructive" });
    load();
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const spotMap = new Map(spots.map((s) => [s.id, s.address]));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Block dates for a spot</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Spot</Label>
            <Select value={form.parking_spot_id} onValueChange={(v) => setForm({ ...form, parking_spot_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select a spot" /></SelectTrigger>
              <SelectContent>
                {spots.map((s) => <SelectItem key={s.id} value={s.id}>{s.address}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          <div>
            <Label>Start date</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <Label>End date</Label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Button onClick={add}><Plus className="h-4 w-4 mr-2" />Add blackout</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Active blackouts</CardTitle></CardHeader>
        <CardContent>
          {blackouts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No blackouts.</p>
          ) : (
            <ul className="divide-y">
              {blackouts.map((b) => (
                <li key={b.id} className="py-3 flex justify-between items-center gap-3">
                  <div>
                    <p className="font-medium">{spotMap.get(b.parking_spot_id) ?? "—"}</p>
                    <p className="text-sm text-muted-foreground">
                      {b.start_date} → {b.end_date}{b.reason ? ` · ${b.reason}` : ""}
                    </p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => remove(b.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlackoutsTab;
